import { Link } from "react-router-dom";
import { Heart, ShieldCheck, Sparkles, Award, ArrowRight, Quote } from "lucide-react";
import { TEAM } from "../data/mock";

const VALUES = [
  {
    icon: Heart,
    title: "Pet-first, always",
    desc: "Every decision — from products to handling — is made with your pet's comfort as the top priority.",
  },
  {
    icon: ShieldCheck,
    title: "Safety & hygiene",
    desc: "Sanitised tools, vet-approved products, and a deep-cleaned studio between every appointment.",
  },
  {
    icon: Sparkles,
    title: "Skilled craftsmanship",
    desc: "Our groomers are certified and trained in breed-specific cuts, styling, and de-shedding techniques.",
  },
  {
    icon: Award,
    title: "Honest pricing",
    desc: "No hidden fees. Transparent quotes shared before we start, every single time.",
  },
];

const STATS = [
  { num: "500+", label: "Happy pets" },
  { num: "8+ yrs", label: "Combined expertise" },
  { num: "4.9★", label: "Average rating" },
  { num: "Bhubaneswar", label: "Proudly local" },
];

export default function About() {
  return (
    <div data-testid="about-page" className="bg-[#3915AA]">
      {/* Hero */}
      <section className="section-padding container-custom">
        <div className="grid lg:grid-cols-12 gap-10 items-center">
          <div className="lg:col-span-7">
            <p className="text-[#C9BCFF] uppercase tracking-[0.25em] text-xs mb-4">About BarknFurs</p>
            <h1 className="font-heading text-5xl md:text-6xl lg:text-7xl font-bold text-white leading-[1.02]">
              Where Bhubaneswar's pets <span className="text-gradient-primary">come to shine</span>.
            </h1>
            <p className="mt-6 text-white/70 text-lg leading-relaxed max-w-xl">
              We started BarknFurs because pet parents in Bhubaneswar deserved a grooming studio
              that was clean, kind and genuinely cared. Today, we're proud to be the go-to spot for
              hundreds of happy dogs and cats.
            </p>
            <div className="mt-9 flex flex-wrap gap-4">
              <Link to="/services" data-testid="about-services-cta" className="btn-primary">
                Our services <ArrowRight className="w-4 h-4" />
              </Link>
              <Link to="/contact" data-testid="about-visit-cta" className="btn-secondary">
                Visit the studio
              </Link>
            </div>
          </div>
          <div className="lg:col-span-5 relative">
            <div className="absolute -top-6 -left-6 w-40 h-40 bg-[#FC5106]/15 blur-3xl rounded-full" />
            <div className="absolute bottom-0 right-0 w-40 h-40 bg-white/15 blur-3xl rounded-full" />
            <div className="relative rounded-3xl overflow-hidden border border-white/10 animate-floatY">
              <img
                src="https://images.pexels.com/photos/29287960/pexels-photo-29287960.jpeg?auto=compress&cs=tinysrgb&w=900"
                alt="Cat with green eyes"
                className="w-full h-[520px] object-cover"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Stats */}
      <section className="container-custom pb-20">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-6">
          {STATS.map((s) => (
            <div
              key={s.label}
              data-testid={`stat-${s.label.replace(/\s+/g, "-").toLowerCase()}`}
              className="min-w-0 rounded-3xl border border-white/10 bg-[#2E0F94] p-4 md:p-8 text-center"
            >
              <p className="font-heading text-3xl md:text-5xl font-bold text-gradient-primary">
                {s.num}
              </p>
              <p className="text-white/60 text-[11px] md:text-sm mt-2 uppercase tracking-wide md:tracking-wider break-words">
                {s.label}
              </p>
            </div>
          ))}
        </div>
      </section>

      {/* Story */}
      <section className="section-padding bg-[#300F9C]">
        <div className="container-custom grid lg:grid-cols-2 gap-12 items-center">
          <div className="relative order-2 lg:order-1">
            <div className="rounded-3xl overflow-hidden border border-white/10">
              <img
                src="https://images.pexels.com/photos/6131576/pexels-photo-6131576.jpeg?auto=compress&cs=tinysrgb&w=1100"
                alt="Groomer at work"
                className="w-full h-[560px] object-cover"
              />
            </div>
          </div>
          <div className="order-1 lg:order-2">
            <p className="text-[#C9BCFF] uppercase tracking-[0.25em] text-xs mb-4">Our story</p>
            <h2 className="font-heading text-4xl md:text-5xl font-bold text-white leading-tight mb-6">
              Built on love, fur, and a lot of belly rubs.
            </h2>
            <div className="space-y-5 text-white/70 leading-relaxed">
              <p>
                BarknFurs Grooming Studio began with a simple frustration — finding a grooming
                place in Bhubaneswar that was clean, calm, used safe products and didn't rush our
                pets. So we built one ourselves.
              </p>
              <p>
                Today our certified groomers handle everything from puppy first-cuts to senior-dog
                spa days. We focus on stress-free handling, hypoallergenic products, and breed-specific
                styling that actually suits your pet.
              </p>
              <p>
                We also bring our studio to your home with our doorstep grooming service — perfect
                for anxious pets or busy pet parents.
              </p>
            </div>
            <div className="mt-8 p-6 rounded-2xl border border-[#FC5106]/30 bg-[#FC5106]/5 relative">
              <Quote className="w-6 h-6 text-[#FC5106] mb-3" />
              <p className="text-white/85 italic leading-relaxed">
                "Pets don't speak our language, so we listen with our eyes, our hands, and patience.
                That's the secret behind every great groom."
              </p>
              <p className="text-[#FC5106] mt-3 text-sm font-semibold">— The BarknFurs Team</p>
            </div>
          </div>
        </div>
      </section>

      {/* Values */}
      <section className="section-padding container-custom section-light">
        <div className="text-center mb-14 max-w-3xl mx-auto">
          <p className="text-[#C9BCFF] uppercase tracking-[0.25em] text-xs mb-4">What we stand for</p>
          <h2 className="font-heading text-4xl md:text-5xl font-bold text-white leading-tight">
            The values behind every wag and purr.
          </h2>
        </div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
          {VALUES.map((v) => (
            <div
              key={v.title}
              data-testid={`value-${v.title.toLowerCase().replace(/[^a-z]+/g, "-")}`}
              className="rounded-3xl border border-white/10 bg-[#2E0F94] p-7 card-lift"
            >
              <div className="bg-[#FC5106]/10 border border-[#FC5106]/30 w-12 h-12 rounded-xl flex items-center justify-center mb-5">
                <v.icon className="w-5 h-5 text-[#FC5106]" />
              </div>
              <h3 className="font-heading text-xl font-bold text-white mb-2">{v.title}</h3>
              <p className="text-white/65 text-sm leading-relaxed">{v.desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Team */}
      <section className="section-padding bg-[#300F9C]">
        <div className="container-custom">
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-12">
            <div className="max-w-2xl">
              <p className="text-[#C9BCFF] uppercase tracking-[0.25em] text-xs mb-4">Meet the team</p>
              <h2 className="font-heading text-4xl md:text-5xl font-bold text-white leading-tight">
                The hands behind every great <span className="text-gradient-primary">grooming day</span>.
              </h2>
            </div>
          </div>
          <div className="grid md:grid-cols-3 gap-6">
            {TEAM.map((t) => (
              <div
                key={t.name}
                data-testid={`team-${t.name.toLowerCase().replace(/\s+/g, "-")}`}
                className="rounded-3xl overflow-hidden border border-white/10 bg-[#2E0F94] card-lift"
              >
                <div className="aspect-[4/5] relative">
                  <img src={t.img} alt={t.name} className="absolute inset-0 w-full h-full object-cover" />
                  <div className="absolute inset-0 bg-gradient-to-t from-[#1F0A66] via-[#1F0A66]/30 to-transparent" />
                </div>
                <div className="p-6">
                  <p className="text-[#C9BCFF] text-xs uppercase tracking-[0.2em] mb-1">{t.role}</p>
                  <h3 className="font-heading text-xl font-bold text-white mb-2">{t.name}</h3>
                  <p className="text-white/65 text-sm leading-relaxed">{t.bio}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}
